import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Menu,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { workflowService } from '../services/workflow.service';
import { Workflow, WorkflowCredential } from '../types/workflow.types';
import { apiService } from '../services/api';

const CREDENTIAL_TYPES: WorkflowCredential['credential_type'][] = [
  'postgres',
  'smtp',
  'gmail',
  'odoo',
  'supabase',
  'imap',
  'http',
  'shopify',
  'hubspot',
  'slack',
  'pipedrive',
  'microsoft365',
  'asana',
  'trello',
  'netsuite',
  'quickbooks',
  'sap',
  'zoom',
  'jira',
  'confluence',
  'discord',
  'microsoft_teams',
  'google_calendar',
  'google_drive',
  'google_sheets',
  'zoho_crm',
  'insightly',
  'cal_com',
  'erpnext',
  'notion',
  'airtable',
  'dropbox',
  'mailchimp',
  'xero',
  'myob',
  'mqtt',
  'custom',
];

type FieldKind = 'text' | 'password' | 'number' | 'boolean' | 'select';
type FieldDef = {
  key: string;
  label: string;
  kind: FieldKind;
  options?: string[];
};

const FIELD_DEFS: Record<WorkflowCredential['credential_type'], FieldDef[]> = {
  postgres: [
    { key: 'host', label: 'Host', kind: 'text' },
    { key: 'port', label: 'Port', kind: 'number' },
    { key: 'database', label: 'Database', kind: 'text' },
    { key: 'user', label: 'User', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'ssl_mode', label: 'SSL Mode', kind: 'select', options: ['disable', 'allow', 'prefer', 'require', 'verify-ca', 'verify-full'] },
  ],
  smtp: [
    { key: 'host', label: 'Host', kind: 'text' },
    { key: 'port', label: 'Port', kind: 'number' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'use_tls', label: 'Use TLS', kind: 'boolean' },
    { key: 'from_email', label: 'From Email', kind: 'text' },
  ],
  gmail: [
    { key: 'access_token', label: 'Access Token (OAuth recommended — use "Connect with Google")', kind: 'password' },
  ],
  odoo: [
    { key: 'base_url', label: 'Base URL', kind: 'text' },
    { key: 'db', label: 'Database', kind: 'text' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
  ],
  supabase: [
    { key: 'base_url', label: 'Base URL', kind: 'text' },
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  imap: [
    { key: 'host', label: 'Host', kind: 'text' },
    { key: 'port', label: 'Port', kind: 'number' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'use_ssl', label: 'Use SSL', kind: 'boolean' },
    { key: 'mailbox', label: 'Mailbox', kind: 'text' },
  ],
  http: [
    { key: 'base_url', label: 'Base URL', kind: 'text' },
    { key: 'auth_token', label: 'Auth Token', kind: 'password' },
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  shopify: [
    { key: 'shop_domain', label: 'Shop Domain (e.g. mystore.myshopify.com)', kind: 'text' },
    { key: 'access_token', label: 'Admin API Access Token', kind: 'password' },
    { key: 'api_version', label: 'API Version (e.g. 2024-01)', kind: 'text' },
  ],
  hubspot: [
    { key: 'access_token', label: 'Private App Token', kind: 'password' },
  ],
  slack: [
    { key: 'token', label: 'Bot OAuth Token (xoxb-...)', kind: 'password' },
  ],
  pipedrive: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  microsoft365: [
    { key: 'access_token', label: 'Access Token (OAuth)', kind: 'password' },
    { key: 'user_id', label: 'User ID or "me"', kind: 'text' },
  ],
  asana: [
    { key: 'access_token', label: 'Personal Access Token', kind: 'password' },
  ],
  trello: [
    { key: 'api_key', label: 'API Key', kind: 'text' },
    { key: 'token', label: 'Token', kind: 'password' },
  ],
  netsuite: [
    { key: 'account_id', label: 'Account ID', kind: 'text' },
    { key: 'consumer_key', label: 'Consumer Key', kind: 'text' },
    { key: 'consumer_secret', label: 'Consumer Secret', kind: 'password' },
    { key: 'token_id', label: 'Token ID', kind: 'text' },
    { key: 'token_secret', label: 'Token Secret', kind: 'password' },
  ],
  quickbooks: [
    { key: 'access_token', label: 'Access Token (OAuth)', kind: 'password' },
    { key: 'realm_id', label: 'Realm ID (Company ID)', kind: 'text' },
  ],
  sap: [
    { key: 'base_url', label: 'Base URL (e.g. https://my.sap.host)', kind: 'text' },
    { key: 'username', label: 'Username', kind: 'text' },
    { key: 'password', label: 'Password', kind: 'password' },
    { key: 'access_token', label: 'Access Token (optional, overrides basic auth)', kind: 'password' },
  ],
  zoom: [
    { key: 'account_id', label: 'Account ID', kind: 'text' },
    { key: 'client_id', label: 'Client ID', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret', kind: 'password' },
  ],
  jira: [
    { key: 'base_url', label: 'Base URL (e.g. https://yoursite.atlassian.net)', kind: 'text' },
    { key: 'email', label: 'Email', kind: 'text' },
    { key: 'api_token', label: 'API Token', kind: 'password' },
  ],
  confluence: [
    { key: 'base_url', label: 'Base URL (e.g. https://yoursite.atlassian.net)', kind: 'text' },
    { key: 'email', label: 'Email', kind: 'text' },
    { key: 'api_token', label: 'API Token', kind: 'password' },
  ],
  discord: [
    { key: 'webhook_url', label: 'Incoming Webhook URL (optional)', kind: 'text' },
    { key: 'bot_token', label: 'Bot Token (optional, for bot operations)', kind: 'password' },
    { key: 'channel_id', label: 'Default Channel ID (for bot)', kind: 'text' },
  ],
  microsoft_teams: [
    { key: 'webhook_url', label: 'Incoming Webhook URL', kind: 'text' },
  ],
  google_calendar: [
    { key: 'access_token', label: 'Access Token (OAuth recommended — use "Connect with Google")', kind: 'password' },
  ],
  google_drive: [
    { key: 'access_token', label: 'Access Token (OAuth recommended — use "Connect with Google")', kind: 'password' },
  ],
  google_sheets: [
    { key: 'access_token', label: 'Access Token (OAuth recommended — use "Connect with Google")', kind: 'password' },
  ],
  zoho_crm: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
    { key: 'refresh_token', label: 'OAuth Refresh Token', kind: 'password' },
    { key: 'client_id', label: 'Client ID', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret', kind: 'password' },
    { key: 'region', label: 'Region (com / eu / in / …)', kind: 'text' },
  ],
  insightly: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
  ],
  cal_com: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
    { key: 'base_url', label: 'Base URL (leave blank for Cal.com cloud)', kind: 'text' },
  ],
  erpnext: [
    { key: 'base_url', label: 'Base URL (e.g. https://myerp.frappe.cloud)', kind: 'text' },
    { key: 'api_key', label: 'API Key', kind: 'text' },
    { key: 'api_secret', label: 'API Secret', kind: 'password' },
  ],
  notion: [
    { key: 'api_token', label: 'Integration Token (secret_…)', kind: 'password' },
  ],
  airtable: [
    { key: 'access_token', label: 'Personal Access Token', kind: 'password' },
    { key: 'base_id', label: 'Default Base ID (appXXX…)', kind: 'text' },
  ],
  dropbox: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
  ],
  mailchimp: [
    { key: 'api_key', label: 'API Key', kind: 'password' },
    { key: 'server_prefix', label: 'Server Prefix (e.g. us21)', kind: 'text' },
  ],
  xero: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
    { key: 'tenant_id', label: 'Tenant ID (Xero organisation UUID)', kind: 'text' },
    { key: 'refresh_token', label: 'Refresh Token (optional, enables auto-refresh)', kind: 'password' },
    { key: 'client_id', label: 'Client ID (optional, for refresh)', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret (optional, for refresh)', kind: 'password' },
  ],
  myob: [
    { key: 'access_token', label: 'OAuth Access Token', kind: 'password' },
    { key: 'api_key', label: 'API Key (x-myobapi-key, from developer.myob.com)', kind: 'password' },
    { key: 'company_file_id', label: 'Default Company File ID (optional, can be set per-node)', kind: 'text' },
    { key: 'cf_token', label: 'Company File Token (base64 of user:password, optional)', kind: 'password' },
    { key: 'refresh_token', label: 'Refresh Token (optional, enables auto-refresh)', kind: 'password' },
    { key: 'client_id', label: 'Client ID (optional, for refresh)', kind: 'text' },
    { key: 'client_secret', label: 'Client Secret (optional, for refresh)', kind: 'password' },
  ],
  mqtt: [
    { key: 'broker_host', label: 'Broker host (e.g. mqtt.example.com)', kind: 'text' },
    { key: 'broker_port', label: 'Broker port', kind: 'number' },
    { key: 'username', label: 'Username (optional)', kind: 'text' },
    { key: 'password', label: 'Password (optional)', kind: 'password' },
    { key: 'client_id', label: 'Client ID (optional — auto-generated if blank)', kind: 'text' },
    { key: 'use_tls', label: 'Use TLS', kind: 'boolean' },
  ],
  custom: [],
};

const TYPE_DEFAULTS: Record<WorkflowCredential['credential_type'], Record<string, any>> = {
  postgres: { host: '', port: 5432, database: '', user: '', password: '', ssl_mode: 'prefer' },
  smtp: { host: '', port: 587, username: '', password: '', use_tls: true, from_email: '' },
  gmail: { access_token: '' },
  odoo: { base_url: '', db: '', username: '', password: '' },
  supabase: { base_url: '', api_key: '' },
  imap: { host: '', port: 993, username: '', password: '', use_ssl: true, mailbox: 'INBOX' },
  http: { base_url: '', auth_token: '', api_key: '' },
  shopify: { shop_domain: '', access_token: '', api_version: '2024-01' },
  hubspot: { access_token: '' },
  slack: { token: '' },
  pipedrive: { api_key: '' },
  microsoft365: { access_token: '', user_id: 'me' },
  asana: { access_token: '' },
  trello: { api_key: '', token: '' },
  netsuite: { account_id: '', consumer_key: '', consumer_secret: '', token_id: '', token_secret: '' },
  quickbooks: { access_token: '', realm_id: '' },
  sap: { base_url: '', username: '', password: '', access_token: '' },
  zoom: { account_id: '', client_id: '', client_secret: '' },
  jira: { base_url: '', email: '', api_token: '' },
  confluence: { base_url: '', email: '', api_token: '' },
  discord: { webhook_url: '', bot_token: '', channel_id: '' },
  microsoft_teams: { webhook_url: '' },
  google_calendar: { access_token: '' },
  google_drive: { access_token: '' },
  google_sheets: { access_token: '' },
  zoho_crm: { access_token: '', refresh_token: '', client_id: '', client_secret: '', region: 'com' },
  insightly: { api_key: '' },
  cal_com: { api_key: '', base_url: '' },
  erpnext: { base_url: '', api_key: '', api_secret: '' },
  notion: { api_token: '' },
  airtable: { access_token: '', base_id: '' },
  dropbox: { access_token: '' },
  mailchimp: { api_key: '', server_prefix: '' },
  xero: { access_token: '', tenant_id: '', refresh_token: '', client_id: '', client_secret: '' },
  myob: { access_token: '', api_key: '', company_file_id: '', cf_token: '', refresh_token: '', client_id: '', client_secret: '' },
  mqtt: { broker_host: '', broker_port: 1883, username: '', password: '', client_id: '', use_tls: false },
  custom: {},
};

export const WorkflowCredentialsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [credentials, setCredentials] = useState<WorkflowCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [shopifyDialogOpen, setShopifyDialogOpen] = useState(false);
  const [shopifyShop, setShopifyShop] = useState('');
  const [shopifyCredName, setShopifyCredName] = useState('Shopify');
  const [shopifyConnecting, setShopifyConnecting] = useState(false);

  const googleButtonRef = useRef<HTMLButtonElement | null>(null);
  const [googleMenuAnchor, setGoogleMenuAnchor] = useState<null | HTMLElement>(null);
  const [googleConnecting, setGoogleConnecting] = useState<string | null>(null);

  type GoogleServiceKey = 'gmail' | 'google_sheets' | 'google_drive' | 'google_calendar';
  const GOOGLE_SERVICE_OPTIONS: Array<{
    key: GoogleServiceKey;
    label: string;
    description: string;
  }> = [
    { key: 'gmail', label: 'Gmail', description: 'Read, send & manage emails' },
    { key: 'google_sheets', label: 'Google Sheets', description: 'Read & write spreadsheets' },
    { key: 'google_drive', label: 'Google Drive', description: 'List, download & upload files' },
    { key: 'google_calendar', label: 'Google Calendar', description: 'Manage events & calendars' },
  ];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkflowCredential | null>(null);
  const [name, setName] = useState('');
  const [credentialType, setCredentialType] = useState<WorkflowCredential['credential_type']>('postgres');
  const [formData, setFormData] = useState<Record<string, any>>({ ...TYPE_DEFAULTS.postgres });
  const [customPairs, setCustomPairs] = useState<Array<{ key: string; value: string }>>([{ key: '', value: '' }]);

  const loadAll = async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const [wf, credsRaw] = await Promise.all([
        workflowService.getWorkflow(id),
        workflowService.getCredentials(id),
      ]);
      setWorkflow(wf);
      const rows: any = Array.isArray(credsRaw)
        ? credsRaw
        : Array.isArray((credsRaw as any)?.results)
          ? (credsRaw as any).results
          : [];
      setCredentials(rows);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load workflow credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Show a one-shot toast when we land back from a Shopify or Google OAuth redirect.
  useEffect(() => {
    const shopifyStatus = searchParams.get('shopify');
    const gmailStatus = searchParams.get('gmail');
    const googleStatus = searchParams.get('google');
    if (shopifyStatus === 'connected') {
      const shop = searchParams.get('shop') || '';
      enqueueSnackbar(
        shop ? `Shopify connected: ${shop}` : 'Shopify connected successfully',
        { variant: 'success' }
      );
    }
    // Legacy Gmail-only success param (kept for back-compat with old start endpoint)
    if (gmailStatus === 'connected') {
      const email = searchParams.get('email') || '';
      enqueueSnackbar(
        email ? `Gmail connected: ${email}` : 'Gmail connected successfully',
        { variant: 'success' }
      );
    }
    // New multi-service Google success param
    if (googleStatus === 'connected') {
      const email = searchParams.get('email') || '';
      const services = (searchParams.get('services') || '').split(',').filter(Boolean);
      const labelMap: Record<string, string> = {
        gmail: 'Gmail',
        google_calendar: 'Calendar',
        google_drive: 'Drive',
        google_sheets: 'Sheets',
      };
      const niceList = services.map((s) => labelMap[s] || s).join(', ');
      const detail = niceList ? ` (${niceList})` : '';
      enqueueSnackbar(
        email
          ? `Google connected as ${email}${detail}`
          : `Google connected${detail}`,
        { variant: 'success' }
      );
    }
    if (shopifyStatus || gmailStatus || googleStatus) {
      // The OAuth callback wrote the new credential row before issuing the
      // redirect, but call loadAll() once more here as a safety net so the
      // table always reflects the just-connected credential without a
      // manual refresh.
      loadAll();
      const next = new URLSearchParams(searchParams);
      next.delete('shopify');
      next.delete('shop');
      next.delete('gmail');
      next.delete('google');
      next.delete('services');
      next.delete('email');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openShopifyConnect = () => {
    setShopifyShop('');
    setShopifyCredName('Shopify');
    setShopifyDialogOpen(true);
  };

  const openGoogleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    setGoogleMenuAnchor(e.currentTarget);
  };
  const closeGoogleMenu = () => setGoogleMenuAnchor(null);

  const startGoogleOAuth = async (service: GoogleServiceKey) => {
    if (!id) return;
    closeGoogleMenu();
    try {
      setGoogleConnecting(service);
      const resp = await apiService.get<{ authorize_url: string; error?: string }>(
        '/integrations/google/oauth/start/',
        { workflow_id: id, services: service }
      );
      if (resp?.authorize_url) {
        window.location.href = resp.authorize_url;
      } else {
        enqueueSnackbar(resp?.error || 'Could not start Google OAuth', { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.error || 'Failed to start Google OAuth',
        { variant: 'error' }
      );
    } finally {
      setGoogleConnecting(null);
    }
  };

  const startShopifyOAuth = async () => {
    if (!id) return;
    const shop = shopifyShop.trim().toLowerCase();
    if (!shop) {
      enqueueSnackbar('Please enter your Shopify store domain', { variant: 'warning' });
      return;
    }
    try {
      setShopifyConnecting(true);
      const resp = await apiService.get<{ authorize_url: string }>(
        '/integrations/shopify/oauth/start/',
        { workflow_id: id, shop, name: shopifyCredName.trim() || 'Shopify' }
      );
      if (resp?.authorize_url) {
        window.location.href = resp.authorize_url;
      } else {
        enqueueSnackbar('Could not start Shopify OAuth', { variant: 'error' });
      }
    } catch (err: any) {
      enqueueSnackbar(
        err?.response?.data?.error || 'Failed to start Shopify OAuth',
        { variant: 'error' }
      );
    } finally {
      setShopifyConnecting(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setName('');
    setCredentialType('postgres');
    setFormData({ ...TYPE_DEFAULTS.postgres });
    setCustomPairs([{ key: '', value: '' }]);
    setDialogOpen(true);
  };

  const openEdit = (item: WorkflowCredential) => {
    setEditing(item);
    setName(item.name);
    setCredentialType(item.credential_type);
    const nextForm = { ...TYPE_DEFAULTS[item.credential_type], ...(item.data || {}) };
    setFormData(nextForm);
    const pairs = Object.entries(item.data || {}).map(([k, v]) => ({ key: k, value: String(v ?? '') }));
    setCustomPairs(pairs.length > 0 ? pairs : [{ key: '', value: '' }]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!id) return;
    if (!name.trim()) {
      enqueueSnackbar('Credential name is required', { variant: 'warning' });
      return;
    }
    const payloadData =
      credentialType === 'custom'
        ? customPairs.reduce((acc, row) => {
            const k = String(row.key || '').trim();
            if (!k) return acc;
            acc[k] = row.value;
            return acc;
          }, {} as Record<string, any>)
        : formData;
    try {
      setSaving(true);
      if (editing) {
        await workflowService.updateCredential(id, editing.id, {
          name: name.trim(),
          credential_type: credentialType,
          data: payloadData,
        } as any);
        enqueueSnackbar('Credential updated', { variant: 'success' });
      } else {
        await workflowService.createCredential(id, {
          name: name.trim(),
          credential_type: credentialType,
          data: payloadData,
          is_active: true,
        } as any);
        enqueueSnackbar('Credential created', { variant: 'success' });
      }
      setDialogOpen(false);
      await loadAll();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || 'Failed to save credential', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleTypeChange = (nextType: WorkflowCredential['credential_type']) => {
    setCredentialType(nextType);
    setFormData({ ...TYPE_DEFAULTS[nextType] });
    setCustomPairs([{ key: '', value: '' }]);
  };

  const handleDelete = async (item: WorkflowCredential) => {
    if (!id) return;
    if (!window.confirm(`Delete credential "${item.name}"?`)) return;
    try {
      await workflowService.deleteCredential(id, item.id);
      enqueueSnackbar('Credential deleted', { variant: 'success' });
      await loadAll();
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data?.error || 'Failed to delete credential', { variant: 'error' });
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate(id ? `/app/workflows/${id}` : '/app/workflows')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">
            Workflow Credentials{workflow?.name ? ` - ${workflow.name}` : ''}
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            ref={googleButtonRef}
            variant="outlined"
            color="primary"
            onClick={openGoogleMenu}
            endIcon={<ArrowDropDownIcon />}
            disabled={Boolean(googleConnecting)}
          >
            {googleConnecting ? 'Redirecting…' : 'Connect with Google'}
          </Button>
          <Button
            variant="outlined"
            color="success"
            onClick={openShopifyConnect}
          >
            Connect with Shopify
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add Credential
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credentials.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">No credentials yet.</TableCell>
              </TableRow>
            ) : (
              credentials.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.credential_type}</TableCell>
                  <TableCell>{new Date(item.updated_at).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(item)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(item)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit Credential' : 'Create Credential'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Credential Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel id="credential-type-label">Type</InputLabel>
            <Select
              labelId="credential-type-label"
              label="Type"
              value={credentialType}
              onChange={(e) => handleTypeChange(e.target.value as any)}
            >
              {CREDENTIAL_TYPES.map((t) => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {credentialType !== 'custom' ? (
            <Box>
              {FIELD_DEFS[credentialType].map((field) => {
                if (field.kind === 'boolean') {
                  return (
                    <FormControlLabel
                      key={field.key}
                      control={
                        <Switch
                          checked={Boolean(formData[field.key])}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, [field.key]: e.target.checked }))
                          }
                        />
                      }
                      label={field.label}
                      sx={{ mb: 1, display: 'block' }}
                    />
                  );
                }
                if (field.kind === 'select') {
                  return (
                    <FormControl key={field.key} fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
                      <Select
                        labelId={`${field.key}-label`}
                        label={field.label}
                        value={formData[field.key] ?? ''}
                        onChange={(e) => setFormData((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {(field.options || []).map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }
                const isOdooPassword =
                  credentialType === 'odoo' && field.key === 'password';
                const legacyKeyStillPresent =
                  isOdooPassword &&
                  typeof formData.api_key === 'string' &&
                  formData.api_key.length > 0;
                return (
                  <TextField
                    key={field.key}
                    label={field.label}
                    type={field.kind === 'password' ? 'password' : field.kind === 'number' ? 'number' : 'text'}
                    value={formData[field.key] ?? ''}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.key]: field.kind === 'number' ? Number(raw || 0) : raw,
                      }));
                    }}
                    fullWidth
                    size="small"
                    sx={{ mb: 2 }}
                    helperText={
                      legacyKeyStillPresent
                        ? 'This credential was created with the old "API Key" field. Leave blank to keep using it, or type a new password to migrate.'
                        : undefined
                    }
                  />
                );
              })}
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Add key-value pairs for custom credentials.
              </Typography>
              {customPairs.map((row, idx) => (
                <Box key={`pair-${idx}`} display="flex" gap={1} sx={{ mb: 1 }}>
                  <TextField
                    label="Key"
                    size="small"
                    fullWidth
                    value={row.key}
                    onChange={(e) => {
                      const next = [...customPairs];
                      next[idx] = { ...row, key: e.target.value };
                      setCustomPairs(next);
                    }}
                  />
                  <TextField
                    label="Value"
                    size="small"
                    fullWidth
                    value={row.value}
                    onChange={(e) => {
                      const next = [...customPairs];
                      next[idx] = { ...row, value: e.target.value };
                      setCustomPairs(next);
                    }}
                  />
                  <Button
                    color="error"
                    onClick={() => setCustomPairs((prev) => prev.filter((_, i) => i !== idx))}
                    disabled={customPairs.length <= 1}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button variant="outlined" onClick={() => setCustomPairs((prev) => [...prev, { key: '', value: '' }])}>
                Add row
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={googleMenuAnchor}
        open={Boolean(googleMenuAnchor)}
        onClose={closeGoogleMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 260 } } }}
      >
        {GOOGLE_SERVICE_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.key}
            onClick={() => startGoogleOAuth(opt.key)}
            disabled={Boolean(googleConnecting)}
            sx={{ py: 1.25 }}
          >
            <ListItemText
              primary={opt.label}
              secondary={opt.description}
              primaryTypographyProps={{ fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
            />
            {googleConnecting === opt.key && (
              <CircularProgress size={16} sx={{ ml: 1 }} />
            )}
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={shopifyDialogOpen}
        onClose={() => setShopifyDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Connect with Shopify</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            We&apos;ll redirect you to Shopify to install the Werk Gang app on your
            store. Once you approve the permissions, the access token is captured
            automatically and saved as a workflow credential — no copy/paste needed.
          </Typography>
          <TextField
            label="Credential name"
            value={shopifyCredName}
            onChange={(e) => setShopifyCredName(e.target.value)}
            fullWidth
            size="small"
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField
            label="Shopify store domain"
            placeholder="my-store.myshopify.com"
            value={shopifyShop}
            onChange={(e) => setShopifyShop(e.target.value)}
            fullWidth
            size="small"
            helperText="Enter your full *.myshopify.com domain (you can copy it from your Shopify admin URL)."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShopifyDialogOpen(false)} disabled={shopifyConnecting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={startShopifyOAuth}
            disabled={shopifyConnecting}
          >
            {shopifyConnecting ? 'Redirecting…' : 'Continue to Shopify'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

